import { Button, Input, Modal } from '@sjuni/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * docs/05-ADMIN-DASHBOARD.md §6.5 — "Confirm destructive actions, and make
 * restore obvious afterwards (everything is soft-deleted)."
 *
 * And §3's harder rule, which is the reason this is a component rather than a
 * `window.confirm`:
 *
 *   "Destructive bulk actions (> 10 records) require typing the record count
 *   to confirm."
 *
 * Typing a number is a deliberately poor UX, on purpose. A dialog you can
 * dismiss by pressing Enter is one you dismiss without reading, and the action
 * behind this one takes out more rows than anybody can check afterwards.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  /** The word on the confirming button — "Delete", "Archive". */
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * How many records the action touches. Over ten, §3 requires the number to
   * be typed. Omit for a single-record action.
   */
  count?: number;
  /** Whether the action can be undone — a soft delete can, a force cannot. */
  reversible?: boolean;
  busy?: boolean;
}

/** §3's threshold. */
export const TYPED_CONFIRMATION_THRESHOLD = 10;

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  count,
  reversible = true,
  busy = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  const [typed, setTyped] = useState('');

  // A dialog reopened for a different action must not inherit the last one's
  // answer, or the second confirmation is free.
  useEffect(() => {
    if (open) {
      setTyped('');
    }
  }, [open]);

  const needsTyping = (count ?? 0) > TYPED_CONFIRMATION_THRESHOLD;
  const confirmed = !needsTyping || typed.trim() === String(count);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,520px)]"
    >
      <div className="space-y-4">
        <p className="text-body-sm text-secondary">{body}</p>

        {reversible ? (
          <p className="text-caption text-muted">{t('confirm.reversible')}</p>
        ) : (
          <p className="rounded-card border-s-4 border-danger bg-surface-alt p-3 text-caption text-danger">
            {t('confirm.irreversible')}
          </p>
        )}

        {needsTyping ? (
          <Input
            label={t('confirm.typeCount', { count })}
            hint={t('confirm.typeCountHint')}
            inputMode="numeric"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>

          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={!confirmed || busy}
            aria-busy={busy || undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
